import React from 'react';
import styles from './ExpandableBook.module.css';
import { BookContent } from '../../services/teachingAgent';

interface ExpandableBookProps {
  book: BookContent;
  expanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onSplitView: () => void;
  onDismiss: () => void;
}

const ExpandableBook: React.FC<ExpandableBookProps> = ({
  book,
  expanded,
  onExpand,
  onCollapse,
  onSplitView,
  onDismiss,
}) => {
  const handleToggle = () => {
    if (expanded) {
      onCollapse();
    } else {
      onExpand();
    }
  };

  // Render a single line of "lite-markdown" (bold / italic / inline code) as
  // React nodes — no dangerouslySetInnerHTML. The book excerpt comes from a
  // model tool call; if the model is ever tricked into emitting `<script>` or
  // event-handler HTML, the previous regex-based markdown processor would
  // inject it verbatim into the DOM. Tokenizing and rendering as React
  // children sidesteps that entirely (React text nodes are auto-escaped).
  const renderLine = (line: string, lineIndex: number): React.ReactNode[] => {
    // Tokenize: alternating runs of plain text and markup-delimited spans.
    // Order matters — `**bold**` must be matched before `*italic*`.
    const tokenRegex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
    const parts = line.split(tokenRegex);
    return parts
      .filter((part) => part !== '' && part !== undefined)
      .map((part, i) => {
        const key = `l${lineIndex}-${i}`;
        if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
          return <strong key={key}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*') && part.length >= 2) {
          return <em key={key}>{part.slice(1, -1)}</em>;
        }
        if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
          return <code key={key}>{part.slice(1, -1)}</code>;
        }
        return <React.Fragment key={key}>{part}</React.Fragment>;
      });
  };

  const renderExcerpt = (content: string) => {
    const lines = content.split('\n');

    return lines.map((line, index) => {
      if (line.startsWith('# ')) {
        return <h1 key={index} className={styles.h1}>{line.slice(2)}</h1>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={index} className={styles.h2}>{line.slice(3)}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={index} className={styles.h3}>{line.slice(4)}</h3>;
      }
      if (line.startsWith('```')) {
        return null; // Skip code fence markers (full code-block rendering can land later)
      }
      if (line.trim() === '') {
        return <br key={index} />;
      }
      return (
        <p key={index} className={styles.paragraph}>
          {renderLine(line, index)}
        </p>
      );
    });
  };

  return (
    <div className={`${styles.container} ${expanded ? styles.expanded : styles.collapsed}`}>
      {/* Header */}
      <div className={styles.header} onClick={handleToggle}>
        <div className={styles.headerContent}>
          <span className={styles.bookIcon}>📚</span>
          <div className={styles.bookInfo}>
            <span className={styles.bookTitle}>{book.chapter}</span>
            <span className={styles.bookSource}>from {book.bookId}</span>
          </div>
        </div>
        <div className={styles.headerActions}>
          {expanded && (
            <button
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                onSplitView();
              }}
              title="Open in Split View"
            >
              ⊞
            </button>
          )}
          <button
            className={styles.actionButton}
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            title="Dismiss"
          >
            ✕
          </button>
          <span className={styles.toggleIcon}>
            {expanded ? '▼' : '▲'}
          </span>
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className={styles.content}>
          <div className={`${styles.excerpt} ${styles.protectedContent}`}>
            {renderExcerpt(book.excerpt)}
          </div>

          {/* Citation */}
          <div className={styles.citation}>
            <span className={styles.citationIcon}>📖</span>
            <span className={styles.citationText}>{book.citation}</span>
            {book.pageReference && (
              <span className={styles.pageRef}>(p. {book.pageReference})</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpandableBook;
